import React, { useEffect, useRef } from 'react';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import styled from 'styled-components';

const StyledModal = styled(Modal)`
  max-width: 90vw;
  .modal-content {
    height: 100vh;
  }
`;

const DocumentPreviewModal = ({ isOpen, toggle, file }) => {
  const createdUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (createdUrlRef.current) {
        try {
          URL.revokeObjectURL(createdUrlRef.current);
        } catch (e) {
          // ignore
        }
        createdUrlRef.current = null;
      }
    };
  }, [file]);

  if (!file) return null;

  // Determine a usable URI for the file
  let fileUri = '';
  let fileName = '';
  let fileType = '';

  if (typeof file === 'string') {
    fileUri = file;
    fileName = file.split('/').pop().split('?')[0]; // Remove query params
  } else if (file.url) {
    fileUri = file.url;
    fileName = file.name || file.url.split('/').pop().split('?')[0];
    fileType = file.type || '';
  } else if (file.document) {
    fileUri = file.document;
    fileName = file.name || file.document.split('/').pop().split('?')[0];
    fileType = file.type || '';
  } else if (file.preview && typeof file.preview === 'string') {
    fileUri = file.preview;
    fileName = file.name || file.preview.split('/').pop().split('?')[0];
    fileType = file.type || '';
  } else if (file.raw) {
    // raw may be a File/Blob or a string URL
    if (typeof file.raw === 'string') {
      fileUri = file.raw;
      fileName = file.name || file.raw.split('/').pop().split('?')[0];
    } else if (file.raw instanceof Blob || file.raw instanceof File) {
      try {
        fileUri = URL.createObjectURL(file.raw);
        createdUrlRef.current = fileUri;
        fileName = file.name || file.raw.name || '';
        fileType = file.type || file.raw.type || '';
      } catch (e) {
        fileUri = '';
      }
    }
  } else if (file instanceof Blob || file instanceof File) {
    try {
      fileUri = URL.createObjectURL(file);
      createdUrlRef.current = fileUri;
      fileName = file.name || '';
      fileType = file.type || '';
    } catch (e) {
      fileUri = '';
    }
  }

  // Infer file type from extension if not provided
  if (!fileType && fileUri) {
    const urlWithoutParams = fileUri.split('?')[0];
    const extension = urlWithoutParams.split('.').pop().toLowerCase();
    
    const extensionMap = {
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'svg': 'image/svg+xml',
      'webp': 'image/webp',
    };
    
    fileType = extensionMap[extension] || '';
  }

  const lowerUri = (fileUri || '').toString().toLowerCase();
  const isPdf = (fileType && fileType.includes('pdf')) || lowerUri.split('?')[0].endsWith('.pdf');
  const isImage = (fileType && fileType.startsWith('image/')) || lowerUri.split('?')[0].match(/\.(jpg|jpeg|png|gif|webp|svg)$/);

  const docs = [
    { uri: fileUri || '', fileType: fileType || '', fileName: fileName || '' },
  ];

  const renderContent = () => {
    if (!fileUri) {
      return <div style={{ padding: 20 }}>Unable to preview this file.</div>;
    }

    if (isPdf) {
      return (
        <iframe
          src={fileUri}
          title={fileName || 'document'}
          width="100%"
          height="100%"
          style={{ border: 'none' }}
          allow="fullscreen"
        />
      );
    }

    if (isImage) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            backgroundColor: '#f8f9fa',
            overflow: 'auto',
          }}
        >
          <img 
            src={fileUri} 
            alt={fileName || ''} 
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentNode.innerHTML = '<div style="padding: 20px; color: #ef4444;">Failed to load image</div>';
            }}
          />
        </div>
      );
    }

    return (
      <DocViewer
        documents={docs}
        pluginRenderers={DocViewerRenderers}
        style={{ height: '100%' }}
        config={{
          header: {
            disableHeader: true,
            disableFileName: true,
            retainURLParams: false,
          },
        }}
      />
    );
  };

  return (
    <StyledModal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>{fileName || 'Preview'}</ModalHeader>
      <ModalBody style={{ padding: 0, height: '100%' }}>{renderContent()}</ModalBody>
    </StyledModal>
  );
};

export default DocumentPreviewModal;
